import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { RouterAddress } from "../../../constants/RouterAddress";
import { ROLE_TYPE_CAREGIVER, ROLE_TYPE_FACILITY_WORKER, ROLE_TYPE_FAMILY_MEMBER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { useResponsive } from "../../../responsive/ResponsiveProvider";
import PublicRoleSelector from "../components/PublicRoleSelector";
import {
  publicMutedTextStyle,
  publicPageContainerStyle,
  publicSectionCardStyle,
} from "../../../components/general/publicPageStyles";
import { WEBSITE_TEMPLATE_OPTIONS } from "../../publishWeb/components/websiteTemplates";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const websiteTemplateNames = WEBSITE_TEMPLATE_OPTIONS.map((template) => template.name).join(", ");

type TutorialItem = {
  title: string;
  summary: string;
  videoUrl?: string;
  steps?: string[];
};

type TutorialSection = {
  title: string;
  items: TutorialItem[];
};

type TutorialRoleContent = {
  badge: string;
  title: string;
  theme: {
    accent: string;
    soft: string;
    border: string;
    chip: string;
  };
  pricingBadge: string;
  tutorialBadge: string;
  tutorialSections: TutorialSection[];
};

const normalizeTutorialFilterValue = (value?: string | null) =>
  String(value || "")
    .trim()
    .toLowerCase();

const createTutorialSlug = (value?: string | null) =>
  normalizeTutorialFilterValue(value).replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const extractYoutubeVideoId = (videoUrl?: string) => {
  const normalized = String(videoUrl || "").trim();
  if (!normalized) {
    return "";
  }

  const matchedWatch = normalized.match(/[?&]v=([^&#]+)/i);
  if (matchedWatch?.[1]) {
    return matchedWatch[1].trim().toLowerCase();
  }

  const matchedShort = normalized.match(/youtu\.be\/([^?&#/]+)/i);
  if (matchedShort?.[1]) {
    return matchedShort[1].trim().toLowerCase();
  }

  return "";
};

const tutorialMatchesFeatureFilter = (section: TutorialSection, item: TutorialItem, featureFilter: string) => {
  if (!featureFilter) {
    return true;
  }

  const normalizedTitle = normalizeTutorialFilterValue(item.title);
  const normalizedSummary = normalizeTutorialFilterValue(item.summary);
  const normalizedSection = normalizeTutorialFilterValue(section.title);
  const titleSlug = createTutorialSlug(item.title);
  const sectionSlug = createTutorialSlug(section.title);

  return normalizedTitle.includes(featureFilter)
    || normalizedSummary.includes(featureFilter)
    || normalizedSection.includes(featureFilter)
    || titleSlug === featureFilter
    || sectionSlug === featureFilter;
};

const tutorialMatchesVideoFilter = (item: TutorialItem, videoFilter: string) => {
  if (!videoFilter) {
    return true;
  }

  const tutorialSlug = createTutorialSlug(item.title);
  const videoId = extractYoutubeVideoId(item.videoUrl);
  const normalizedVideoUrl = normalizeTutorialFilterValue(item.videoUrl);

  return tutorialSlug === videoFilter
    || videoId === videoFilter
    || normalizedVideoUrl.includes(videoFilter);
};

const buildTutorialQueryUrl = (input: {
  role: string;
  feature?: string;
  video?: string;
}) => {
  const params = new URLSearchParams();
  params.set("role", input.role);
  if (input.feature) {
    params.set("feature", input.feature);
  }
  if (input.video) {
    params.set("video", input.video);
  }
  return `${window.location.origin}${RouterAddress.TUTORIALS_PAGE}?${params.toString()}`;
};

const getTutorialRoleContent = (providerBadge: string, websiteLabel: string): Record<string, TutorialRoleContent> => ({
  [ROLE_TYPE_PROVIDER]: {
    badge: providerBadge,
    title: `${providerBadge} Tutorials`,
    theme: {
      accent: "#0f3d63",
      soft: "#eef6ff",
      border: "#cfe0f1",
      chip: "#e38b2c",
    },
    pricingBadge: "Pricing Available",
    tutorialBadge: "Guided Tutorials",
    tutorialSections: [
      {
        title: "Automation Tools",
        items: [
          {
            title: "Generate a Negotiated Care Plan from an Assessment",
            summary: "Upload any medicaid or Private Pay assessment document to create a care plan draft that you can review and edit. The NCP template follows latest DSHS template.",
            videoUrl: "https://youtu.be/vCZAatjdbek",
            steps: [
              "Open Care Plan from the provider dashboard or resident profile.",
              "If the resident does not have an imported assessment yet, use the Assessment to Care Plan automation dialog.",
              "Upload the assessment document and start the automation flow.",
              "Review the generated care plan draft, then continue into related plan sections.",
            ],
          },
          {
            title: "Generate Behavior and Intervention Care Plan",
            summary: "Use the care plan draft to generate behavior and intervention care plans that you can review, edit, and save to the resident binder.",
            videoUrl: "https://youtu.be/vCZAatjdbek",
            steps: [
              "Open the resident Care Plan page after the care plan draft is available.",
              "Review the behavior and intervention section generated from the draft.",
              "Open the preliminary service care plan section.",
              "Edit, save, and generate documents when the content is ready.",
            ],
          },
          {
            title: "Generate Preliminary Service Care Plan",
            summary: "Use the care plan draft to generate preliminary service care plans that you can review, edit, and save to the resident binder.",
            videoUrl: "https://youtu.be/vCZAatjdbek",
            steps: [
              "Open the resident Care Plan page after the care plan draft is available.",
              "Review the behavior and intervention section generated from the draft.",
              "Open the preliminary service care plan section.",
              "Edit, save, and generate documents when the content is ready.",
            ],
          },
          {
            title: "Summarize or Compare Resident Documents",
            summary: "Summarize resident documents like assessments, care plans, or other important files to quickly understand the key information. You can also compare two documents to see the differences side by side and get a summary of how they differ.",
            videoUrl: "https://youtu.be/KmMkNwhmc24",
            steps: [
              "Open the resident profile.",
              "Use Assessment Tools to choose Summary, Compare Assessments, or Compare Care Plans.",
              "Upload a new file or choose the needed binder documents.",
              "Review the generated output and save it to the binder when useful.",
            ],
          },
          {
            title: "Create Resident Assessment and Delegation Forms from Documents",
            summary: "Registered nurses can complete a resident assessment from files like Clinical results, MAR Sheet, assessment notes, and other relevant documents. System will also generate delegation forms based on the assessment content to help with care organization and task delegation.",
            videoUrl: "https://youtu.be/DT1sDI2lcic",
            steps: [
              "Open the resident Assessment page from the resident profile.",
              "Use Create Draft Assessment from Documents (AI) to upload the source files.",
              "Review the generated sections, save draft updates, and mark the assessment complete when ready.",
              "Generate the final document and save it to the resident binder.",
            ],
          },
        ],
      },
      {
        title: "Core Access",
        items: [
          {
            title: `Set Up a ${websiteLabel}`,
            summary: `Set up your home website, choose a design, and keep it updated. Pick from 2 templates.`,
            videoUrl: "",
            steps: [
              "Open Website from the provider dashboard.",
              "Review the website status, link, and quick actions.",
              "Use Edit Template to open the website editor.",
              "Update the content, preview the site, and publish when ready.",
            ],
          },
          {
            title: "Organize Binder and Resident Documents",
            summary: "Keep home and resident files in one place so important documents are easy to find later.",
            videoUrl: "",
            steps: [
              "Open the resident profile or home documents area.",
              "Upload the source files you want to keep in the binder.",
              "Use resident folders to organize assessments, care plans, and delegation outputs.",
              "Return later to preview, download, or continue working from the same files.",
            ],
          },
          {
            title: "Use CBHS/IBSS Tracker and Monthly Reports",
            summary: "Track daily behaviors in one place and generate monthly reports faster when you need them.",
            videoUrl: "https://youtu.be/6CkwXjTpX0I",
            steps: [
              "Open CBHS/IBSS Tracker from the provider dashboard or resident workflow.",
              "Add the daily behavior entries you want to keep for the resident.",
              "Review the saved entries over time to keep the log organized.",
              "Generate the monthly report when you are ready to review or share the summary.",
            ],
          },
          {
            title: "Write Progress Notes",
            summary: "Capture daily resident notes in one provider workflow so important updates stay easy to review later.",
            videoUrl: "https://youtu.be/6CkwXjTpX0I",
            steps: [
              "Open the resident profile or provider workflow where daily notes are available.",
              "Create a new daily progress note for the resident.",
              "Save the note after reviewing the details you entered.",
              "Return later to review previous notes and continue documentation.",
            ],
          },
          {
            title: "Create Incident Reports",
            summary: "Document resident incidents in a structured workflow and keep the follow-up record easier to manage.",
            videoUrl: "https://youtu.be/6CkwXjTpX0I",
            steps: [
              "Open Incident Reports from the provider workflow.",
              "Create a new incident report and enter the required details.",
              "Review the report for accuracy and save it.",
              "Return later to review incident history and any needed follow-up.",
            ],
          },
        ],
      },
      {
        title: "Follow-Up Workflows",
        items: [
          {
            title: "Manage Reminders",
            summary: "Track deadlines, repeating tasks, and home or resident reminders in one place.",
            videoUrl: "",
            steps: [
              "Open the reminders page from the header.",
              "Create a new reminder or start from a template.",
              "Set the due date or recurring schedule.",
              "Return later to complete, update, or review reminder history.",
            ],
          },
        ],
      },
    ],
  },
  [ROLE_TYPE_NURSE]: {
    badge: "Nurse",
    title: "Nurse Tutorials",
    theme: {
      accent: "#0b5cab",
      soft: "#f4f9ff",
      border: "#d8e7f7",
      chip: "#0ea5e9",
    },
    pricingBadge: "Pricing Available",
    tutorialBadge: "Guided Tutorials",
    tutorialSections: [
      {
        title: "Assessment",
        items: [
          {
            title: "Resident Assessment automation from documents",
            summary: "Upload resident assessment source documents like Clinical results, MAR Sheet, assessment notes, and other relevant files to generate a draft assessment. The system will also create Body Chart and delegation forms based on the assessment content to help with care organization and task delegation.",
            videoUrl: "https://www.youtube.com/watch?v=mmefuUYCfHw",
            steps: [
              "Open the resident assessment page.",
              "Use Create Draft Assessment from Documents (AI) to upload the assessment files you want to use.",
              "Review the generated sections and save updates as needed.",
              "Mark the assessment complete and generate the final document when ready.",
            ],
          },
          {
            title: "Generate a Negotiated Care Plan from an Assessment",
            summary: "Upload any medicaid or Private Pay assessment document to create a care plan draft that you can review and edit. The NCP template follows latest DSHS template.",
            videoUrl: "https://youtu.be/yNsCv8IF0yg",
            steps: [
              "Open the resident assessment page.",
              "Use Create Draft Assessment from Documents (AI) to upload the assessment files you want to use.",
              "Review the generated sections and save updates as needed.",
              "Mark the assessment complete and generate the final document when ready.",
            ],
          },
          {
            title: "Organize Assessment Documents",
            summary: "Keep assessment files and finished documents easier to track for each resident.",
            videoUrl: "",
            steps: [
              "Open the resident assessment page.",
              "Use View Documents or Previous Assessments from the action menu.",
              "Review uploaded source files and generated outputs after processing finishes.",
              "Use the document actions to preview, download, or continue the workflow later.",
            ],
          },
        ],
      },
      {
        title: "Delegation",
        items: [
          {
            title: "Manage Nurse Delegations",
            summary: "Create and organize resident delegation forms in one place.",
            videoUrl: "https://www.youtube.com/watch?v=mmefuUYCfHw",
            steps: [
              "Open the resident delegation page.",
              "Choose the delegation form you need.",
              "Update the form sections, save your draft, and return later if needed.",
              "Generate or save the final form after the review is complete.",
            ],
          },
          {
            title: "Manage Podiatrist - Foot and Nail Care",
            summary: "Track podiatrist foot and nail care details with the resident record so follow-up stays organized.",
            videoUrl: "",
            steps: [
              "Open the resident podiatrist page from the nurse workspace.",
              "Review or enter the foot and nail care details for the resident.",
              "Save updates so the care record stays current.",
              "Return later to continue follow-up or document the next visit.",
            ],
          },
        ],
      },
    ],
  },
  [ROLE_TYPE_MHP]: {
    badge: "MHP",
    title: "Mental Health Professional Tutorials",
    theme: {
      accent: "#0b5cab",
      soft: "#f4f9ff",
      border: "#d8e7f7",
      chip: "#0ea5e9",
    },
    pricingBadge: "Pricing Available",
    tutorialBadge: "Guided Tutorials",
    tutorialSections: [
      {
        title: "Assessment",
        items: [
          {
            title: "Resident Assessment automation from documents",
            summary: "Upload resident assessment source documents like clinical results, MAR sheets, assessment notes, and related records to generate a draft assessment that you can review and update.",
            videoUrl: "https://www.youtube.com/watch?v=mmefuUYCfHw",
            steps: [
              "Open the resident assessment page.",
              "Use Create Draft Assessment from Documents (AI) to upload the files you want to use.",
              "Review the generated sections and save updates as needed.",
              "Mark the assessment complete and generate the final document when ready.",
            ],
          },
          {
            title: "Generate a Negotiated Care Plan from an Assessment",
            summary: "Create a care plan draft from assessment content, then review and edit the sections that matter for mental health and psychosocial support.",
            videoUrl: "https://youtu.be/yNsCv8IF0yg",
            steps: [
              "Open the resident assessment or care plan page.",
              "Start the care plan generation flow from the latest assessment content.",
              "Review the generated sections and update the wording where needed.",
              "Save the draft and continue into related plan sections.",
            ],
          },
          {
            title: "Organize Assessment Documents",
            summary: "Keep assessment files and finished documents easier to review for each resident.",
            videoUrl: "",
            steps: [
              "Open the resident assessment page.",
              "Use View Documents or Previous Assessments from the action menu.",
              "Review uploaded source files and generated outputs after processing finishes.",
              "Use the document actions to preview, download, or continue the workflow later.",
            ],
          },
        ],
      },
      {
        title: "Care Planning",
        items: [
          {
            title: "Review psychosocial and behavior-focused planning content",
            summary: "Use generated care plan content as a starting point for psychosocial, cognitive, and behavior-focused planning work.",
            videoUrl: "",
            steps: [
              "Open the resident care plan page.",
              "Review the psychosocial and behavior-related sections generated from the assessment.",
              "Edit the narrative and intervention details as needed.",
              "Save the updated content and continue with document generation when ready.",
            ],
          },
        ],
      },
    ],
  },
  [ROLE_TYPE_CAREGIVER]: {
    badge: "Caregiver",
    title: "Caregiver Tutorials",
    theme: {
      accent: "#166534",
      soft: "#f3fbf5",
      border: "#cfe8d6",
      chip: "#16a34a",
    },
    pricingBadge: "Pricing Available",
    tutorialBadge: "Guided Tutorials",
    tutorialSections: [
      {
        title: "Daily Workflow",
        items: [
          {
            title: "Manage Reminders",
            summary: "Set up repeating reminders so important caregiver tasks stay easy to see.",
            videoUrl: "",
            steps: [
              "Open the reminders page.",
              "Create the reminder you need.",
              "Set the due schedule or recurring pattern.",
              "Return later to review and update reminder status.",
            ],
          },
          {
            title: "Stay on Top of Daily Work",
            summary: "Keep everyday follow-up tasks and helpful information easier to manage.",
            videoUrl: "",
            steps: [
              "Review reminders at the start of the day.",
              "Open documents when you need supporting information.",
              "Update tasks as work is completed.",
              "Return later to plan the next round of follow-up.",
            ],
          },
        ],
      },
      {
        title: "Documents",
        items: [
          {
            title: "Keep Documents Organized",
            summary: "Upload and organize important files you need for daily care.",
            videoUrl: "",
            steps: [
              "Open the documents page.",
              "Upload the files you need to keep handy.",
              "Review the list and organize the information.",
              "Return anytime to update or review those files.",
            ],
          },
        ],
      },
    ],
  },
  [ROLE_TYPE_FACILITY_WORKER]: {
    badge: "Facility Worker",
    title: "Facility Worker Tutorials",
    theme: { accent: "#166534", soft: "#f3fbf5", border: "#cfe8d6", chip: "#16a34a" },
    pricingBadge: "No subscription required",
    tutorialBadge: "Getting Started",
    tutorialSections: [
      {
        title: "Assigned Facility Tasks",
        items: [
          {
            title: "Get Started as a Facility Worker",
            summary: "Learn how Facility Workers view and take action on assigned facility tasks. This role does not include resident or clinical workflows and does not require a subscription.",
            videoUrl: "",
            steps: [
              "Sign in with the Facility Worker role.",
              "Review the facility dashboard and your assigned tasks.",
              "Open assigned tasks and take the available task actions.",
            ],
          },
        ],
      },
    ],
  },
  [ROLE_TYPE_FAMILY_MEMBER]: {
    badge: "Family Member",
    title: "Family Tutorials",
    theme: {
      accent: "#7c2d12",
      soft: "#fff7ed",
      border: "#f1dbc7",
      chip: "#c76b11",
    },
    pricingBadge: "Pricing Available",
    tutorialBadge: "Guided Tutorials",
    tutorialSections: [
      {
        title: "Review",
        items: [
          {
            title: "Review Important Documents",
            summary: "Keep resident documents easy to open and review when you need them.",
            videoUrl: "",
            steps: [
              "Open the family workspace.",
              "Review the resident documents you need.",
              "Keep the most important information easy to find.",
              "Return later whenever you need to revisit those files.",
            ],
          },
        ],
      },
      {
        title: "Organization",
        items: [
          {
            title: "Stay Organized",
            summary: "Keep important information in one place instead of scattered messages or folders.",
            videoUrl: "",
            steps: [
              "Open the document list.",
              "Review what is already available.",
              "Keep the information you need easier to revisit.",
              "Use the same place each time you need an update.",
            ],
          },
        ],
      },
    ],
  },
});

const resolveTutorialRole = (roleParam: string | null, availableRoles: string[] = []) => {
  const normalizedRole = String(roleParam || "").trim().toLowerCase();
  const supportedRoles = availableRoles.length > 0 ? availableRoles : [ROLE_TYPE_PROVIDER, ROLE_TYPE_NURSE, ROLE_TYPE_CAREGIVER];
  return supportedRoles.includes(normalizedRole) ? normalizedRole : ROLE_TYPE_PROVIDER;
};

const Tutorials = () => {
  const authExperience = useAuthExperience();
  const availableRoleTypes = useMemo(() => authExperience.roleOptions.map((role) => role.value), [authExperience.roleOptions]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRole, setSelectedRole] = useState<string | null>(() => resolveTutorialRole(searchParams.get("role"), availableRoleTypes));
  const [copiedKey, setCopiedKey] = useState<string>("");
  const mobile = useResponsive().isMobile;
  const tutorialRoleContent = useMemo(
    () => getTutorialRoleContent(authExperience.nurseWorkspace.providerLabel, authExperience.discovery.jobWebsiteLabel),
    [authExperience.discovery.jobWebsiteLabel, authExperience.nurseWorkspace.providerLabel],
  );
  const roleContent = selectedRole ? tutorialRoleContent[selectedRole] : null;
  const featureFilter = createTutorialSlug(searchParams.get("feature")) || normalizeTutorialFilterValue(searchParams.get("feature"));
  const videoFilter = normalizeTutorialFilterValue(searchParams.get("video"));
  const filteredTutorialSections = roleContent
    ? roleContent.tutorialSections
      .map((section: TutorialSection) => ({
        ...section,
        items: section.items.filter((item: TutorialItem) =>
          tutorialMatchesFeatureFilter(section, item, featureFilter) && tutorialMatchesVideoFilter(item, videoFilter)
        ),
      }))
      .filter((section: TutorialSection) => section.items.length > 0)
    : [];
  const tutorialCount = filteredTutorialSections.reduce((count: number, section: TutorialSection) => count + section.items.length, 0);
  const hasTutorialFilters = !!featureFilter || !!videoFilter;

  useEffect(() => {
    const resolvedRole = resolveTutorialRole(searchParams.get("role"), availableRoleTypes);
    setSelectedRole((currentRole) => (currentRole === resolvedRole ? currentRole : resolvedRole));
  }, [availableRoleTypes, searchParams]);

  useEffect(() => {
    const resolvedRole = selectedRole || ROLE_TYPE_PROVIDER;
    const currentRoleParam = String(searchParams.get("role") || "").trim().toLowerCase();
    if (currentRoleParam === resolvedRole) {
      return;
    }

    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set("role", resolvedRole);
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, selectedRole, setSearchParams]);

  const copyText = async (text: string, key: string) => {
    if (!text || !navigator.clipboard?.writeText) {
      return;
    }

    await navigator.clipboard.writeText(text);
    setCopiedKey(key);
    window.setTimeout(() => {
      setCopiedKey((current) => (current === key ? "" : current));
    }, 2000);
  };

  const clearTutorialFilters = () => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete("feature");
    nextSearchParams.delete("video");
    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <div style={publicPageContainerStyle}>
      <div>
        <PublicRoleSelector
          selectedRole={selectedRole}
          onChange={setSelectedRole}
          title="Choose A Role To See Tutorials"
          compact
          inline
        />
      </div>

      {!roleContent ? (
        <div className="mt-3" style={publicSectionCardStyle}>
          <div style={{ color: "#0f3d63", fontSize: "1.2rem", fontWeight: 700 }}>Select a role to see tutorials</div>
          <p style={{ ...publicMutedTextStyle, margin: "0.75rem 0 0" }}>
            The tutorials on this page only appear after you choose the role you want to learn.
          </p>
        </div>
      ) : (
        <div className="mt-3" style={publicSectionCardStyle}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.75rem",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div
                className="inline-flex align-items-center gap-2 px-3 py-2 border-round-2xl mb-2"
                style={{
                  backgroundColor: roleContent.theme.soft,
                  border: `1px solid ${roleContent.theme.border}`,
                  color: roleContent.theme.accent,
                  fontWeight: 700,
                }}
              >
                <i className="pi pi-play-circle" style={{ color: roleContent.theme.chip }} />
                {roleContent.badge}
              </div>
              <div className="flex flex-wrap align-items-center gap-2">
                <h2 style={{ color: roleContent.theme.accent, margin: 0, fontSize: "1.8rem" }}>{roleContent.title}</h2>
                {hasTutorialFilters ? (
                  <button
                    type="button"
                    onClick={clearTutorialFilters}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      borderRadius: "999px",
                      border: "1px solid #fdba74",
                      background: "#fff7ed",
                      color: "#9a3412",
                      padding: "0.55rem 0.95rem",
                      fontSize: "0.9rem",
                      fontWeight: 800,
                      cursor: "pointer",
                      boxShadow: "0 8px 18px rgba(249, 115, 22, 0.16)",
                    }}
                  >
                    <i className="pi pi-times-circle" />
                    Show all tutorials
                  </button>
                ) : null}
              </div>
              <div style={{ ...publicMutedTextStyle, marginTop: "0.35rem" }}>
                {tutorialCount} tutorials for this role.
              </div>
            </div>
          </div>

          <div
            style={{
              border: `1px solid ${roleContent.theme.border}`,
              borderRadius: "22px",
              overflow: "hidden",
              background: "#ffffff",
              boxShadow: "0 14px 32px rgba(15, 61, 99, 0.06)",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: mobile ? "1fr" : "minmax(170px, 220px) 1fr",
                borderBottom: `1px solid ${roleContent.theme.border}`,
                background: roleContent.theme.soft,
              }}
            >
              <div style={{ padding: "1rem 1.1rem", fontWeight: 800, color: roleContent.theme.accent, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.8rem" }}>
                Role
              </div>
              <div style={{ padding: "1rem 1.1rem", fontWeight: 800, color: roleContent.theme.accent, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: "0.8rem" }}>
                Tutorial List
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: mobile ? "1fr" : "minmax(170px, 220px) 1fr",
              }}
            >
              <div
                style={{
                  padding: "1.15rem 1.1rem",
                  borderRight: mobile ? "none" : `1px solid ${roleContent.theme.border}`,
                  borderBottom: mobile ? `1px solid ${roleContent.theme.border}` : "none",
                  background: roleContent.theme.soft,
                }}
              >
                <div style={{ color: roleContent.theme.accent, fontWeight: 800, fontSize: "1rem" }}>{roleContent.badge}</div>
                <div style={{ ...publicMutedTextStyle, marginTop: "0.35rem", fontSize: "0.9rem" }}>
                  {tutorialCount} tutorials
                </div>
                {hasTutorialFilters ? (
                  <div className="flex flex-wrap align-items-center gap-2" style={{ marginTop: "0.5rem" }}>
                    <div style={{ ...publicMutedTextStyle, fontSize: "0.84rem" }}>
                      Filtered by
                      {featureFilter ? ` feature: ${searchParams.get("feature")}` : ""}
                      {featureFilter && videoFilter ? " | " : ""}
                      {videoFilter ? `video: ${searchParams.get("video")}` : ""}
                    </div>
                  </div>
                ) : null}
              </div>

              <div style={{ padding: "1.15rem 1.1rem" }}>
                {filteredTutorialSections.length === 0 ? (
                  <div
                    style={{
                      border: `1px dashed ${roleContent.theme.border}`,
                      borderRadius: "18px",
                      background: roleContent.theme.soft,
                      padding: "1.1rem",
                    }}
                  >
                    <div style={{ color: roleContent.theme.accent, fontWeight: 800, fontSize: "1rem" }}>No tutorials matched this filter</div>
                    <div style={{ ...publicMutedTextStyle, marginTop: "0.45rem" }}>
                      Try a different `feature` or `video` query value, or remove the filter to see the full tutorial list.
                    </div>
                  </div>
                ) : (
                <div className="grid">
                  {filteredTutorialSections.map((section: TutorialSection) => (
                    <div key={section.title} className="col-12">
                      <div
                        style={{
                          border: `1px solid ${roleContent.theme.border}`,
                          borderRadius: "18px",
                          background: "#fcfdff",
                          padding: "0.95rem 1rem",
                          marginBottom: "0.9rem",
                        }}
                      >
                        <div
                          style={{
                            color: roleContent.theme.accent,
                            fontWeight: 800,
                            fontSize: "0.98rem",
                            marginBottom: "0.75rem",
                          }}
                        >
                          {section.title}
                        </div>

                        <div
                          style={{
                            border: `1px solid ${roleContent.theme.border}`,
                            borderRadius: "16px",
                            overflow: "hidden",
                            background: "#ffffff",
                          }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: mobile ? "1fr" : "minmax(220px, 1.15fr) minmax(280px, 1.6fr) minmax(140px, 0.75fr)",
                              background: roleContent.theme.soft,
                              borderBottom: `1px solid ${roleContent.theme.border}`,
                            }}
                          >
                            <div style={{ padding: "0.85rem 1rem", fontWeight: 800, color: roleContent.theme.accent, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                              Feature
                            </div>
                            <div style={{ padding: "0.85rem 1rem", fontWeight: 800, color: roleContent.theme.accent, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em", borderLeft: mobile ? "none" : `1px solid ${roleContent.theme.border}` }}>
                              Short Description
                            </div>
                            <div style={{ padding: "0.85rem 1rem", fontWeight: 800, color: roleContent.theme.accent, fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.04em", borderLeft: mobile ? "none" : `1px solid ${roleContent.theme.border}` }}>
                              Video
                            </div>
                          </div>

                          {section.items.map((item: TutorialItem, index: number) => {
                            const hasVideo = !!String(item.videoUrl || "").trim();
                            const itemSlug = createTutorialSlug(item.title);
                            const tutorialShareUrl = buildTutorialQueryUrl({
                              role: selectedRole || ROLE_TYPE_PROVIDER,
                              feature: itemSlug,
                              video: itemSlug,
                            });
                            const copiedTutorialKey = `tutorial:${itemSlug}`;
                            const copiedVideoKey = `video:${itemSlug}`;
                            return (
                              <div
                                key={item.title}
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: mobile ? "1fr" : "minmax(220px, 1.15fr) minmax(280px, 1.6fr) minmax(140px, 0.75fr)",
                                  borderBottom: index === section.items.length - 1 ? "none" : `1px solid ${roleContent.theme.border}`,
                                }}
                              >
                                <div
                                  style={{
                                    padding: "1rem",
                                    color: "#0f172a",
                                    fontWeight: 800,
                                    borderBottom: mobile ? `1px solid ${roleContent.theme.border}` : "none",
                                  }}
                                >
                                  <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
                                    <div>{item.title}</div>
                                    <div className="flex flex-wrap gap-2">
                                      <button
                                        type="button"
                                        onClick={() => { void copyText(tutorialShareUrl, copiedTutorialKey); }}
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "0.4rem",
                                          borderRadius: "999px",
                                          border: `1px solid ${roleContent.theme.border}`,
                                          background: copiedKey === copiedTutorialKey ? roleContent.theme.soft : "#ffffff",
                                          color: roleContent.theme.accent,
                                          padding: "0.38rem 0.72rem",
                                          fontSize: "0.8rem",
                                          fontWeight: 700,
                                          cursor: "pointer",
                                        }}
                                      >
                                        <i className={`pi ${copiedKey === copiedTutorialKey ? "pi-check" : "pi-link"}`} />
                                        {copiedKey === copiedTutorialKey ? "Copied" : "Copy"}
                                      </button>
                                    </div>
                                  </div>
                                </div>

                                <div
                                  style={{
                                    padding: "1rem",
                                    color: "#64748b",
                                    fontSize: "0.93rem",
                                    lineHeight: 1.6,
                                    borderLeft: mobile ? "none" : `1px solid ${roleContent.theme.border}`,
                                    borderBottom: mobile ? `1px solid ${roleContent.theme.border}` : "none",
                                  }}
                                >
                                  {item.summary}
                                </div>

                                <div
                                  style={{
                                    padding: "1rem",
                                    display: "flex",
                                    alignItems: mobile ? "flex-start" : "center",
                                    justifyContent: mobile ? "flex-start" : "center",
                                    borderLeft: mobile ? "none" : `1px solid ${roleContent.theme.border}`,
                                  }}
                                >
                                  {hasVideo ? (
                                    <div className="flex flex-wrap align-items-center justify-content-center gap-2" style={{ width: mobile ? "100%" : "auto" }}>
                                      <a
                                        href={item.videoUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: "0.45rem",
                                          minWidth: mobile ? "100%" : "auto",
                                          padding: "0.55rem 0.9rem",
                                          borderRadius: "999px",
                                          background: roleContent.theme.soft,
                                          border: `1px solid ${roleContent.theme.border}`,
                                          color: roleContent.theme.accent,
                                          textDecoration: "none",
                                          fontWeight: 700,
                                          fontSize: "0.9rem",
                                        }}
                                      >
                                        <i className="pi pi-youtube" style={{ color: roleContent.theme.chip }} />
                                        Watch Video
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => { void copyText(String(item.videoUrl || "").trim(), copiedVideoKey); }}
                                        style={{
                                          display: "inline-flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          gap: "0.4rem",
                                          minWidth: mobile ? "100%" : "auto",
                                          borderRadius: "999px",
                                          border: `1px solid ${roleContent.theme.border}`,
                                          background: copiedKey === copiedVideoKey ? roleContent.theme.soft : "#ffffff",
                                          color: roleContent.theme.accent,
                                          padding: "0.55rem 0.8rem",
                                          fontSize: "0.86rem",
                                          fontWeight: 700,
                                          cursor: "pointer",
                                        }}
                                      >
                                        <i className={`pi ${copiedKey === copiedVideoKey ? "pi-check" : "pi-copy"}`} />
                                        {copiedKey === copiedVideoKey ? "Copied" : "Copy"}
                                      </button>
                                    </div>
                                  ) : (
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "0.45rem",
                                        minWidth: mobile ? "100%" : "auto",
                                        padding: "0.55rem 0.9rem",
                                        borderRadius: "999px",
                                        background: "#f8fafc",
                                        border: "1px dashed #cbd5e1",
                                        color: "#64748b",
                                        fontWeight: 700,
                                        fontSize: "0.9rem",
                                      }}
                                    >
                                      <i className="pi pi-video" />
                                      Coming Soon
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tutorials;
